import React from 'react';
import { Link } from 'react-router-dom';
import PortfolioStatusBadges from './PortfolioStatusBadges';
import { formatLpDateRange } from '../../utils/workSamplesPage';
import { buildUserProfileAssignmentUrl } from '../../utils/userProfileUrl';
import { formatCommentReasonLabels } from '../../constants/workSampleFlags';
import FlagCorrectionSla from './FlagCorrectionSla';
import { getCommentFlagCorrectionSla } from '../../utils/workSampleCorrectionSla';

function CoverSheetCard({ cover }) {
  if (!cover) {
    return (
      <div className="ws-review-cover ws-review-cover--empty">
        <p className="profile-sheet-muted">
          Cover sheet is generated when the instructor submits this portfolio for review.
        </p>
      </div>
    );
  }

  return (
    <div className="ws-review-cover">
      <div className="ws-review-cover-grid">
        <div>
          <span className="ws-review-cover-label">Student</span>
          <strong>{cover.student_name || '—'}</strong>
        </div>
        <div>
          <span className="ws-review-cover-label">Learning period</span>
          <strong>{cover.learning_period_title || '—'}</strong>
          {(cover.learning_period_start || cover.learning_period_end) && (
            <span className="ws-review-cover-sub">
              {formatLpDateRange(cover.learning_period_start, cover.learning_period_end)}
            </span>
          )}
        </div>
        <div>
          <span className="ws-review-cover-label">Track</span>
          <strong>{cover.track_label || '—'}</strong>
        </div>
        <div>
          <span className="ws-review-cover-label">Vendor</span>
          <strong>{cover.vendor_label || '—'}</strong>
        </div>
        <div>
          <span className="ws-review-cover-label">Grade</span>
          <strong>{cover.grade_level || '—'}</strong>
        </div>
        <div>
          <span className="ws-review-cover-label">Evidence</span>
          <strong>
            {cover.evidence_count ?? 0} item{(cover.evidence_count ?? 0) === 1 ? '' : 's'}
          </strong>
          <span className="ws-review-cover-sub">
            {cover.form_count ?? 0} form{(cover.form_count ?? 0) === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </div>
  );
}

function EvidenceList({ portfolio, profileLink }) {
  const forms = portfolio?.forms || [];
  const documents = portfolio?.documents || [];
  if (!forms.length && !documents.length) {
    return <p className="profile-sheet-muted ws-review-empty">No evidence in this portfolio yet.</p>;
  }

  return (
    <ul className="ws-review-evidence-list">
      {forms.map((form) => {
        const included = form.included_submissions || [];
        const submissionId = included[0]?.id || form.latest_submission?.id || null;
        const profileUrl =
          profileLink?.userId && form.id
            ? buildUserProfileAssignmentUrl({
                userId: profileLink.userId,
                userGroupsPeriodsId: profileLink.userGroupsPeriodsId,
                formId: form.id,
                relationshipId: form.relationship_id,
                submissionId,
                from: profileLink.returnPath,
              })
            : null;

        return (
          <li
            key={form.id}
            className={[
              'ws-review-evidence-item',
              form.has_open_flags ? 'ws-review-evidence-item--flagged' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="ws-review-evidence-main">
              <strong>{form.title || 'Form'}</strong>
              <span className="ws-review-evidence-meta">
                {included.length
                  ? `${included.length} in portfolio`
                  : form.has_submission
                    ? 'Submitted — not included'
                    : 'Awaiting submission'}
              </span>
            </div>
            <div className="ws-review-evidence-side">
              {form.has_open_flags ? (
                <span className="ws-explorer-badge ws-explorer-badge--flag">
                  {form.open_flag_count === 1 ? '1 flag' : `${form.open_flag_count} flags`}
                </span>
              ) : (
                <span className="ws-review-evidence-ok" aria-hidden>
                  ✓
                </span>
              )}
              {profileUrl ? (
                <Link
                  to={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ws-review-open-link"
                >
                  Review
                </Link>
              ) : null}
            </div>
          </li>
        );
      })}
      {documents.map((doc) => (
        <li key={doc.id} className="ws-review-evidence-item">
          <div className="ws-review-evidence-main">
            <strong>{doc.title || 'Document'}</strong>
            <span className="ws-review-evidence-meta">{doc.category || 'Upload'}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PortfolioFlagRows({ flags, asOfDate = null }) {
  const openPortfolioFlags = (flags || []).filter(
    (f) => String(f.type) === 'portfolio_flag'
  );
  if (!openPortfolioFlags.length) return null;

  return (
    <div className="ws-review-portfolio-flags">
      <p className="profile-sheet-label">Portfolio flags</p>
      {openPortfolioFlags.map((flag) => {
        const reasons = formatCommentReasonLabels(flag.metadata);
        return (
          <div key={flag.id} className="ws-explorer-flag-row">
            <strong>{flag.metadata?.title || reasons.join(', ') || 'Portfolio issue'}</strong>
            {flag.body ? <span>{flag.body}</span> : null}
            <FlagCorrectionSla flag={flag} asOfDate={asOfDate} />
          </div>
        );
      })}
    </div>
  );
}

function CorrectionSlaSummary({ flags, asOfDate = null }) {
  const open = (flags || []).filter((f) => {
    const status = String(f.metadata?.flag_status || 'open').toLowerCase();
    return !status || status === 'open';
  });
  if (!open.length) return null;

  const slas = open.map((f) => getCommentFlagCorrectionSla(f, asOfDate || new Date())).filter(Boolean);
  if (!slas.length) return null;

  const overdue = slas.filter((s) => s.isOverdue).length;
  const dueToday = slas.filter((s) => s.isDueToday).length;
  let text;
  if (overdue > 0) {
    text =
      overdue === 1
        ? '1 open flag is overdue for TOR correction.'
        : `${overdue} open flags are overdue for TOR correction.`;
  } else if (dueToday > 0) {
    text =
      dueToday === 1
        ? '1 open flag is due today for TOR correction.'
        : `${dueToday} open flags are due today for TOR correction.`;
  } else {
    const soonest = Math.min(...slas.map((s) => s.businessDaysLeft));
    const unit = soonest === 1 ? 'business day' : 'business days';
    text = `Open flags: TOR has up to ${soonest} ${unit} left on the soonest correction window.`;
  }

  return <p className="ws-review-sla-summary">{text}</p>;
}

export default function PortfolioReviewPanel({
  lp,
  portfolio,
  profileLink = null,
  canSubmit = false,
  canReview = false,
  busy = false,
  asOfDate = null,
  onSubmit,
  onStartReview,
  onPass,
}) {
  if (!portfolio) return null;

  const lifecycle = portfolio.lifecycle_status || portfolio.display_status || portfolio.status;
  const openFlags = portfolio.open_flag_count || 0;
  const cover = portfolio.metadata?.cover_sheet_snapshot || null;
  const isDraft = !lifecycle || lifecycle === 'draft';
  const isPassed = lifecycle === 'passed';
  const canSubmitNow = canSubmit && isDraft && !busy;
  const canStartReviewNow = canReview && !isPassed && !busy;
  const canPassNow = canReview && !isDraft && openFlags === 0 && !isPassed && !busy;
  const allOpenFlags = portfolio.open_flags || portfolio.flags || [];

  return (
    <div className="ws-review-panel">
      <div className="ws-review-panel-head">
        <div>
          <p className="profile-sheet-label">Portfolio package</p>
          <PortfolioStatusBadges
            lifecycleStatus={lifecycle}
            openFlagCount={openFlags}
            attentionLabel={portfolio.attention_label}
          />
        </div>
        <div className="ws-review-panel-actions">
          {canSubmitNow ? (
            <button
              type="button"
              className="profile-sheet-btn profile-sheet-btn--primary"
              disabled={busy}
              onClick={() => onSubmit?.(portfolio.id)}
            >
              Submit for review
            </button>
          ) : null}
          {canStartReviewNow ? (
            <button
              type="button"
              className="profile-sheet-btn profile-sheet-btn--secondary"
              disabled={busy || lifecycle === 'in_review'}
              onClick={() => onStartReview?.(portfolio.id)}
            >
              {lifecycle === 'in_review' ? 'In review' : 'Start review'}
            </button>
          ) : null}
          {canReview && !isDraft ? (
            <button
              type="button"
              className="profile-sheet-btn profile-sheet-btn--primary"
              disabled={!canPassNow}
              title={
                openFlags > 0
                  ? 'Resolve all open flags before marking passed'
                  : undefined
              }
              onClick={() => onPass?.(portfolio.id)}
            >
              Mark passed
            </button>
          ) : null}
        </div>
      </div>

      <p className="ws-review-panel-hint profile-sheet-muted">
        {isDraft
          ? canReview
            ? 'Draft preview — you can start review before the instructor submits the full packet.'
            : 'Submit when forms and evidence are ready. Open flags can be addressed while the portfolio stays submitted.'
          : openFlags > 0
            ? 'Lifecycle stays submitted while flags are open. Address flagged submissions, then VDM can mark passed.'
            : 'Review the cover sheet and evidence below. Flag individual submissions from the student profile.'}
      </p>

      <CorrectionSlaSummary flags={allOpenFlags} asOfDate={asOfDate} />

      <div className="ws-review-section">
        <p className="profile-sheet-label">Cover sheet</p>
        <CoverSheetCard cover={cover} />
      </div>

      <div className="ws-review-section">
        <p className="profile-sheet-label">Evidence</p>
        <EvidenceList portfolio={portfolio} profileLink={profileLink} />
      </div>

      <PortfolioFlagRows flags={allOpenFlags} asOfDate={asOfDate} />
    </div>
  );
}

